import { Building2, ChevronRight, Pencil, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import type { Hallway, HallwayInventoryData } from "~/types/wms";

interface HallwayListPanelProps {
  hallways: HallwayInventoryData[];
  selectedHallwayId: string | null;
  onSelectHallway: (hallwayId: string) => void;
  onEditHallway?: (hallway: Hallway) => void;
  onDeleteHallway?: (hallway: Hallway) => void;
  isLoading?: boolean;
}

export function HallwayListPanel({
  hallways,
  selectedHallwayId,
  onSelectHallway,
  onEditHallway,
  onDeleteHallway,
  isLoading,
}: HallwayListPanelProps) {
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4" />
            Pasillos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Building2 className="h-4 w-4" />
          Pasillos ({hallways.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 p-2">
        {hallways.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Building2 className="h-8 w-8 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">
              No hay pasillos en esta bodega
            </p>
          </div>
        ) : (
          hallways.map((hallwayData, index) => {
            const isSelected = selectedHallwayId === hallwayData.hallway.id;
            const occupancy = hallwayData.occupancyPercent;

            return (
              <button
                key={hallwayData.hallway.id}
                onClick={() => onSelectHallway(hallwayData.hallway.id)}
                style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                className={cn(
                  "w-full rounded-lg border p-3 text-left transition-all duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                  "hover:border-accent-foreground/20 hover:bg-accent",
                  isSelected &&
                    "border-primary bg-primary/5 ring-1 ring-primary"
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold">
                      {hallwayData.hallway.code}
                    </span>
                    {hallwayData.hallway.name && (
                      <span className="text-xs text-muted-foreground">
                        {hallwayData.hallway.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {onEditHallway && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditHallway(hallwayData.hallway);
                        }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                    {onDeleteHallway && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteHallway(hallwayData.hallway);
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                    <ChevronRight
                      className={cn(
                        "h-4 w-4 text-muted-foreground transition-transform",
                        isSelected && "translate-x-0.5 text-primary"
                      )}
                    />
                  </div>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <Progress value={occupancy} className="h-1.5 flex-1" />
                  <span className="w-10 text-right text-xs text-muted-foreground">
                    {occupancy.toFixed(0)}%
                  </span>
                </div>

                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{hallwayData.racks.length} racks</span>
                  <span>
                    {hallwayData.occupiedPositions}/{hallwayData.totalPositions}{" "}
                    pos.
                  </span>
                </div>
              </button>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
